import React, { useEffect, useRef } from 'react';
import _merge from 'lodash/merge';
import { getContext, useCombinedRefs } from 'common';
import Elevation, { ElevationProps } from 'components/Elevation';
import { useDropzone, DropzoneOptions, FileRejection } from 'react-dropzone';

import useMeasure from 'react-use-measure';
import ResizeObserver from 'resize-observer-polyfill';

import Styled from './styles';

export const FileUploadContext = React.createContext<any | null>({});

export interface FileUploadProps extends DropzoneOptions {
  /** Content of FileUpload */
  children?: React.ReactNode;
  /** Props which will provided to the wrapper element. */
  wrapperProps?: React.ComponentPropsWithRef<any>;
  /** If true, it indicates there is some error occurred. <FileUploadIcon> component will display AlarmCircleIcon. FileUpload background when dragging will be gray color. */
  error?: boolean;
  /** Callback which will return the ref of the wrapper element. See react-dropzone documentation for more information. */
  wrapperRef?: (arg: React.RefObject<HTMLElement>) => void;
  /** Callback which will return the ref of the input element. See react-dropzone documentation for more information. */
  inputRef?: (arg: React.RefObject<HTMLElement>) => void;
  /** Callback which will return the acceptedFiles every time it changes. See react-dropzone documentation for more information. */
  acceptedFiles?: (args: File[]) => void;
  /** Callback which will return the rejectedFiles every time it changes. See react-dropzone documentation for more information. */
  fileRejections?: (args: FileRejection[]) => void;
  /** Callback which will return the dragging status (isDragActive, isDragAccept, isDragReject) every time them change. See react-dropzone documentation for more information. */
  dragStatus?: (arg: { isDragActive: boolean, isDragAccept: boolean, isDragReject: boolean }) => void;
  /** Callback which will return the open function. Please check this page for the usage of `open` function: https://react-dropzone.js.org/#section-opening-file-dialog-programmatically */
  open?: (arg: () => void) => void;
  /** To override the theme please refer to the "Readme" tab to learn more. See react-dropzone documentation for more information. */
  theme?: any;
  /** Override the Elevation properties. See Elevation component for details. */
  elevationProps?: Partial<ElevationProps>;
}

/** Uncontrolled FileUpload component.
 * Any other props will be provided to the useDropzone hook of react-dropzone. Please check the documentation for the usage: https://react-dropzone.js.org.
 * To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.)
 */
const FileUpload = (props: FileUploadProps) => {
  const {
    children,
    error,
    wrapperProps,
    wrapperRef,
    inputRef: passedInputRef,
    acceptedFiles: passedAcceptedFiles,
    fileRejections: passedFileRejections,
    open: passedOpen,
    theme: themeProp,
    dragStatus,
    elevationProps,
    ...otherProps
  } = props;

  const { ref: passedElevationRef, ...otherElevationProps } = elevationProps || {};
  const { style: wrapperStyle, ...otherWrapperProps } = wrapperProps || {};

  const isRTL = getContext(({ RTL }) => RTL);
  const $theme = getContext(({ theme }) => theme.FILE_UPLOAD);
  const resolvedTheme = _merge({}, $theme, themeProp);
  const {
    acceptedFiles,
    fileRejections,
    open,
    rootRef, // Ref to the `<div>`
    inputRef, // Ref to the `<input>`
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject
  } = useDropzone({ noClick: true, ...otherProps });

  useEffect(() => !!passedAcceptedFiles && passedAcceptedFiles(acceptedFiles), [acceptedFiles]);
  useEffect(() => !!passedFileRejections && passedFileRejections(fileRejections), [fileRejections]);

  useEffect(() => {
    !!rootRef && wrapperRef && wrapperRef(rootRef);
    !!passedInputRef && passedInputRef(inputRef);
    !!passedOpen && passedOpen(open);
  }, [rootRef, inputRef, open]);

  useEffect(() => {
    !!dragStatus && dragStatus({ isDragActive, isDragAccept, isDragReject });
  }, [isDragActive, isDragAccept, isDragReject]);

  /** Measure height and set minHeight to ensure element won't shrink vertically.
   * The reason is when isDragActive is true, the content may change. The nature of this component will try to fit content.
   * In this case the entire Drag zone will shrink and it may cause the drag zone looks like 'flickering' on edge cases.
   */
  const [measureRef, { height }] = useMeasure({ polyfill: ResizeObserver });
  const measuredHeightRef = useRef(height);

  if (!isDragActive) {
    measuredHeightRef.current = height;
  }
  const combinedElevationRef = useCombinedRefs(passedElevationRef, measureRef);
  const resolvedWrapperStyle = {
    minHeight: isDragActive ? measuredHeightRef.current : 'auto',
    ...wrapperStyle
  };

  return (
    <Elevation ref={combinedElevationRef} {...otherElevationProps}>
      <Styled.Container
        {...getRootProps({
          isDragActive,
          isDragAccept,
          isDragReject,
          $theme: resolvedTheme,
          $rtl: isRTL,
          style: resolvedWrapperStyle,
          error,
          ...otherWrapperProps
        })}
      >
        <Styled.Input {...getInputProps()} />
        <FileUploadContext.Provider
          value={{
            acceptedFiles,
            fileRejections,
            open,
            getRootProps,
            getInputProps,
            isDragActive,
            isDragAccept,
            isDragReject,
            $theme: resolvedTheme,
            rootRef,
            inputRef,
            error
          }}
        >
          {!!children && children}
        </FileUploadContext.Provider>
      </Styled.Container>
    </Elevation>
  );
};

FileUpload.displayName = 'FileUpload';

FileUpload.defaultProps = {
  children: undefined,
  wrapperProps: {},
  wrapperRef: undefined,
  inputRef: undefined,
  acceptedFiles: undefined,
  fileRejections: undefined,
  dragStatus: undefined,
  open: undefined,
  error: false,
  elevationProps: {},
  theme: {}
};
export default FileUpload;
